import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Row,
  Select,
} from "antd";
import { CheckCircle, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ConfirmationModal from "../../Components/CustomModals/ConfirmationModal";
import {
  formatNumbers,
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";

interface CreditBillingStatementFormProps {
  add?: boolean;
  edit?: boolean;
  review?: boolean;
  approval?: boolean;
}

function computeTotals(total: number, vatType: string, walletAmount: number) {
  let vatEx: number;
  let vat: number;
  let whTax: number;
  let baseGrandTotal: number;

  switch (vatType) {
    case "vat_in": {
      vatEx          = total / 1.12;
      vat            = total - vatEx;
      whTax          = vatEx * 0.02;
      baseGrandTotal = total - whTax;
      break;
    }
    case "vat_ex": {
      vatEx          = total;
      vat            = total * 0.12;
      whTax          = vatEx * 0.02;
      baseGrandTotal = total + vat - whTax;
      break;
    }
    case "zero_rated":
    default: {
      vatEx          = total;
      vat            = 0;
      whTax          = vatEx * 0.02;
      baseGrandTotal = total - whTax;
      break;
    }
  }

  const walletUsed      = Math.min(walletAmount, baseGrandTotal);
  const grandTotal      = Math.max(0, baseGrandTotal - walletUsed);
  const remainingWallet = Math.max(0, walletAmount - walletUsed);

  return { total, vatEx, vat, whTax, grandTotal, remainingWallet };
}

const CreditBillingStatementForm: React.FC<CreditBillingStatementFormProps> = ({
  add,
  edit,
  review,
  approval,
}) => {
  const [loading,              setLoading]           = useState("");
  const [showModal,            setShowModal]         = useState("");
  const [errorMessage,         setErrorMessage]      = useState("");
  const [successMessage,       setSuccessMessage]    = useState("");
  const [cancelReason,         setCancelReason]      = useState("");
  const [companyId,            setCompanyId]         = useState<number | null>(null);
  const [wallet,               setWallet]            = useState<any>(null);
  const [filterConfig,         setFilterConfig]      = useState({
    company_id: "",
    date_from:  "",
    date_to:    "",
  });
  const [companies,            setCompanies]            = useState<any[]>([]);
  const [dailyTransactionData, setDailyTransactionData] = useState<any[]>([]);
  const [selectedCompanyId,    setSelectedCompanyId]    = useState<number | null>(null);
  const [form]                                          = Form.useForm();

  const { id: credit_billing_id } = useParams<{ id: string }>();

  const isReadOnly = review || approval;

  const selectedCompany = companies.find((c: any) => Number(c.id) === selectedCompanyId);
  const isBranch        = selectedCompany?.is_branch == 1;
  const vatType         = selectedCompany?.vat_type ?? "zero_rated";

  function clearFields() {
    form.resetFields();
    setDailyTransactionData([]);
    setSelectedCompanyId(null);
    setCompanyId(null);
    setWallet(null);
  }

  const rawTotal = dailyTransactionData.reduce((sum: number, item: any) => {
    return sum + (Number(item?.remaining_balance) || 0);
  }, 0);

  const { total, vatEx, vat, whTax, grandTotal, remainingWallet } = computeTotals(
    rawTotal,
    vatType,
    wallet?.available_amount ?? 0
  );

  async function submitForm(data: any) {
    const formattedItemsArray = dailyTransactionData.map((item: any) => ({
      transaction_id:   item.id,
      transaction_date: item.transaction_date
        ? dayjs(item.transaction_date).format("YYYY-MM-DD")
        : "",
      ...(isBranch
        ? { test_type: item.test_type, quantity: item.quantity }
        : { rwt_no: item.rwt_no }),
      amount: item.remaining_balance,
    }));

    const formattedData: any = {
      ...data,
      date_from:         dayjs(data.date_from).format("YYYY-MM-DD"),
      date_to:           dayjs(data.date_to).format("YYYY-MM-DD"),
      itemsArray:        formattedItemsArray,
      total,
      vat_ex:            vatEx,
      vat,
      wh_tax:            whTax,
      _2307_calculation: grandTotal,
      remaining_wallet:  remainingWallet,
    };

    if (edit) formattedData.credit_billing_id = credit_billing_id;

    setLoading("submit");
    try {
      const response = await postRequest(
        add ? "credit_billings/create" : "credit_billings/update",
        formattedData,
        true
      );

      if (response.success) {
        setSuccessMessage(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          setShowModal("");
          setSuccessMessage("");
          navigateTo("/credit-billing-statement");
        }, 1500);
      } else {
        setErrorMessage(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            `Failed to ${add ? "create" : "update"} credit billing statement.`
        );
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function approveCancelRequest() {
    setLoading("cancel");
    try {
      const response = await postRequest(
        "credit_billings/change_status",
        { credit_billing_id, action: "approve_cancel_request" },
        true
      );
      if (response.success) {
        setSuccessMessage(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          setShowModal("");
          setSuccessMessage("");
          navigateTo("/credit-billing-statement");
        }, 1500);
      } else {
        setShowModal("");
        setErrorMessage(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            "Failed to approve cancel request for credit billing statement."
        );
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function cancelRequest() {
    if (!cancelReason.trim()) {
      message.warning("Please provide a reason for cancellation.");
      return;
    }
    setLoading("cancel");
    try {
      const response = await postRequest(
        "credit_billings/change_status",
        { credit_billing_id, action: "cancel_request", reason: cancelReason },
        true
      );
      if (response.success) {
        setSuccessMessage(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          setShowModal("");
          setSuccessMessage("");
          navigateTo("/credit-billing-statement");
        }, 1500);
      } else {
        setShowModal("");
        setErrorMessage(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            "Failed to cancel credit billing statement."
        );
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function fetchCompanies() {
    setCompanies([]);
    const response = await getRequest(`companies/get`, {}, true);
    if (response.success) setCompanies(response.data.data);
  }

  async function fetchWallet(company_id: number) {
    setWallet(null);
    const response = await getRequest(`advance_payments/get`, { company_id }, true);
    if (response.success) {
      const data = response.data.data;
      setWallet(Array.isArray(data) ? (data[0] ?? null) : (data ?? null));
    }
  }

  async function fetchUnpaidBillings() {
    setDailyTransactionData([]);
    const response = await getRequest(
      `daily_transactions/get`,
      { status: "pending", ...filterConfig },
      true
    );
    if (response.success) setDailyTransactionData(response.data.data);
  }

  async function fetchCreditBillingData() {
    if (!credit_billing_id) return;
    setLoading("fetch");
    const response = await getRequest("credit_billings/get", { credit_billing_id, ...filterConfig }, true);
    if (response.success) {
      const data = response.data.data[0];

      const baseFields: any = {
        company_id:   Number(data.company_id),
        statement_no: data.statement_no,
        date_from:    data.date_from ? dayjs(data.date_from) : null,
        date_to:      data.date_to   ? dayjs(data.date_to)   : null,
      };

      setSelectedCompanyId(Number(data.company_id));
      setCompanyId(Number(data.company_id));

      if (data.items && data.items.length > 0) {
        setDailyTransactionData(data.items.map((item: any) => ({
          id:                item.transaction_id,
          transaction_date:  item.transaction_date,
          rwt_no:            item.rwt_no,
          test_type:         item.test_type,
          quantity:          item.quantity,
          remaining_balance: Number(item.amount ?? item.remaining_balance),
        })));
      }

      form.setFieldsValue(baseFields);
    }
    setLoading("");
  }

  useEffect(() => {
    fetchCompanies();
    if (edit || review || approval) fetchCreditBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterConfig.company_id) fetchUnpaidBillings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  useEffect(() => {
    if (companyId) fetchWallet(companyId);
    else setWallet(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const pageTitle = add      ? "Add Credit Billing Statement"
    : edit     ? "Edit Credit Billing Statement"
    : review   ? "Review Credit Billing Statement"
    : approval ? "Approve Cancel Credit Billing Statement"
    :            "View Credit Billing Statement";

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader title={pageTitle} withDateRangeFilter={false} withAddButton={false} withSearchBtn={false} />
          </Col>

          {loading !== "fetch" && (
            <div className="form-container w-full">
              <Col span={24}>
                <Form form={form} onFinish={submitForm} layout="vertical">
                  <Row className="w-full" gutter={[10, 10]}>

                    <Col xs={24} lg={12}>
                      <Form.Item label="Company Name" name="company_id"
                        rules={[{ required: true, message: "This field is required." }]}>
                        <Select
                          placeholder="Select company"
                          showSearch
                          allowClear
                          onChange={(value) => {
                            const id = value ? Number(value) : null;
                            setCompanyId(id);
                            setSelectedCompanyId(id);
                            setFilterConfig((prev) => ({ ...prev, company_id: value ?? "" }));
                            if (!value) setWallet(null);
                          }}
                          optionFilterProp="label"
                          disabled={isReadOnly || edit}
                          options={companies.map((info: any) => ({
                            label: info.name,
                            value: Number(info.id),
                          }))}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item label="Billing Statement Number" name="statement_no"
                        rules={[{ required: true, message: "This field is required." }]}>
                        <Input disabled={isReadOnly} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Date From"
                        name="date_from"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          format="YYYY-MM-DD"
                          placeholder="Select start date"
                          disabled={isReadOnly}
                          onChange={(_, dateString) =>
                            setFilterConfig((prev) => ({ ...prev, date_from: dateString as string }))
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Date To"
                        name="date_to"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          format="YYYY-MM-DD"
                          placeholder="Select end date"
                          disabled={isReadOnly}
                          onChange={(_, dateString) =>
                            setFilterConfig((prev) => ({ ...prev, date_to: dateString as string }))
                          }
                          disabledDate={(current) => {
                            const dateFrom = form.getFieldValue("date_from");
                            return dateFrom ? current && current.isBefore(dateFrom, "day") : false;
                          }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <span className="edit-label mb-2">
                        <label className="text-red-500 me-1">*</label>List of Unpaid Billings:
                      </span>

                      <div className="overflow-auto max-h-[600px]">

                        {dailyTransactionData.length > 0 && !isBranch && (
                          <table className="w-full border border-collapse min-w-[700px]">
                            <thead>
                              <tr className="bg-primary text-white font-bold border">
                                <th className="p-2 border w-[50px]">#</th>
                                <th className="p-2 border">Transaction Date</th>
                                <th className="p-2 border">RWT No</th>
                                <th className="p-2 border">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailyTransactionData.map((item: any, index: number) => (
                                <tr key={item.id} className="border bg-white">
                                  <td className="p-2 border text-center text-sm text-gray-500">{index + 1}</td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.transaction_date} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.rwt_no} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.remaining_balance} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {dailyTransactionData.length > 0 && isBranch && (
                          <table className="w-full border border-collapse min-w-[700px]">
                            <thead>
                              <tr className="bg-primary text-white font-bold border">
                                <th className="p-2 border w-[50px]">#</th>
                                <th className="p-2 border">Test Type</th>
                                <th className="p-2 border">Quantity</th>
                                <th className="p-2 border">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailyTransactionData.map((item: any, index: number) => (
                                <tr key={item.id} className="border bg-white">
                                  <td className="p-2 border text-center text-sm text-gray-500">{index + 1}</td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.test_type} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.quantity} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.remaining_balance} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {dailyTransactionData.length === 0 && (
                          <div className="text-center text-gray-400 py-6 border rounded">
                            {selectedCompanyId
                              ? "No unpaid billings found for this company."
                              : "Select a company to load unpaid billings."}
                          </div>
                        )}

                      </div>

                      <div className="flex flex-col items-end gap-1 mt-3 text-sm">
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">Total:</span>
                          <strong>₱ {formatNumbers(total)}</strong>
                        </div>
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">Vat Ex:</span>
                          <strong>₱ {formatNumbers(vatEx)}</strong>
                        </div>
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">
                            Vat {vatType === "zero_rated" ? "(0%)" : "(12%)"}:
                          </span>
                          <strong>₱ {formatNumbers(vat)}</strong>
                        </div>
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">WHTax (2%):</span>
                          <strong>₱ {formatNumbers(whTax)}</strong>
                        </div>
                        {wallet && add && (
                          <>
                            <div className="flex justify-between w-full max-w-xs">
                              <span className="text-gray-500">Wallet Balance:</span>
                              <strong>₱ {formatNumbers(wallet.available_amount ?? 0)}</strong>
                            </div>
                            <div className="flex justify-between w-full max-w-xs">
                              <span className="text-gray-500">Remaining Wallet:</span>
                              <strong>₱ {formatNumbers(remainingWallet)}</strong>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between w-full max-w-xs border-t pt-1 mt-1">
                          <span className="font-bold">Grand Total:</span>
                          <strong className="text-primary">₱ {formatNumbers(grandTotal)}</strong>
                        </div>
                      </div>
                    </Col>

                    <Col lg={24} className="mt-5 flex justify-end gap-3">
                      <Button onClick={() => { navigateTo("/credit-billing-statement"); clearFields(); }}>
                        {isReadOnly ? "Close" : "Cancel"}
                      </Button>

                      {(add || edit) && (
                        <Button htmlType="submit" type="primary" loading={loading === "submit"} disabled={dailyTransactionData.length === 0}>
                          {add ? "Submit" : "Update"}
                        </Button>
                      )}

                      {review && (
                        <Button
                          danger
                          loading={loading === "cancel"}
                          onClick={() => { setCancelReason(""); setShowModal("cancel_request"); }}
                        >
                          Cancel Request
                        </Button>
                      )}

                      {approval && (
                        <Button
                          danger
                          loading={loading === "cancel"}
                          onClick={() => { setCancelReason(""); setShowModal("cancelled"); }}
                        >
                          Approve Cancel Request
                        </Button>
                      )}
                    </Col>

                  </Row>
                </Form>
              </Col>
            </div>
          )}
        </Row>
      </Layout>

      <ConfirmationModal
        title="Are you sure you want to approve the cancel request for this credit billing statement?"
        icon={<CheckCircle className="text-green-500 mt-5" size={60} />}
        showModal={showModal === "cancelled"}
        onCancel={() => setShowModal("")}
        footer={
          <div className="flex justify-center gap-3 mt-5">
            <Button onClick={() => setShowModal("")}>Cancel</Button>
            <Button type="primary" loading={loading === "cancel"} onClick={approveCancelRequest}>
              Approve
            </Button>
          </div>
        }
      />

      <ConfirmationModal
        title="Are you sure you want to cancel this credit billing statement?"
        icon={<XCircle className="text-red-500 mt-5" size={60} />}
        showModal={showModal === "cancel_request"}
        onCancel={() => setShowModal("")}
        footer={
          <div className="flex flex-col gap-3 mt-5">
            <Input.TextArea
              rows={3}
              placeholder="Enter reason for cancellation..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <div className="flex justify-center gap-3">
              <Button onClick={() => setShowModal("")}>Cancel</Button>
              <Button danger loading={loading === "cancel"} onClick={cancelRequest}>
                Submit
              </Button>
            </div>
          </div>
        }
      />

      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />

      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default CreditBillingStatementForm;